import React, { useState, useEffect } from "react";
import { auth, db, storage } from "../firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { HexColorPicker } from "react-colorful";

const countries = [
  "Philippines","United States","United Kingdom","Canada","Australia",
  "Japan","South Korea","Singapore","France","Germany","Italy","Spain"
];

function Create() {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.pathname === "/update";

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  // PROFILE PHOTO
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState("");
  const [existingPhoto, setExistingPhoto] = useState("");

  // BACKGROUND
  const [bgImage, setBgImage] = useState(null);
  const [bgPreview, setBgPreview] = useState("");
  const [existingBg, setExistingBg] = useState("");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [bgColor, setBgColor] = useState("#ffffff");
  const [bgMode, setBgMode] = useState("image");

  const [overlay, setOverlay] = useState("none");

  // INFO
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [isCountryValid, setIsCountryValid] = useState(true);
  const closeDropdown = () => {
  setFilteredCountries([]);
};

const dropdownRef = React.useRef(null);

useEffect(() => {
  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      closeDropdown();
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  // 🔐 AUTH
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) navigate("/");
      else setUser(u);
    });
    return () => unsub();
  }, [navigate]);

  // 📸 PROFILE PHOTO
  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  };

  // 🖼 BACKGROUND IMAGE
const handleBgImage = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  setBgImage(file);
  setBgPreview(URL.createObjectURL(file));

  // 🔥 switch mode to IMAGE
  setBgMode("image");

  // clear color mode
  setBgColor("#ffffff");
};

const handleSelectColorMode = () => {
  setBgMode("color");

  // remove image when switching to color
  setBgImage(null);
  setBgPreview("");
};

const handleColorChange = (color) => {
  setBgColor(color);
  setBgMode("color");

  setBgImage(null);
  setBgPreview("");
};

  // 🌍 COUNTRY
  const handleCountryChange = (value) => {
    setCountry(value);

    const matches = countries.filter(c =>
      c.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredCountries(matches);
    setIsCountryValid(value === "" || countries.includes(value));
  };

  const handleSelectCountry = (c) => {
    setCountry(c);
    setFilteredCountries([]);
    setIsCountryValid(true);
  };

  // 🔄 LOAD EXISTING DATA
  useEffect(() => {
    if (!user) return;

    const load = async () => {
      const snap = await getDoc(doc(db, "users", user.uid));

      if (snap.exists()) {
        const data = snap.data();

        setName(data.name || "");
        setCountry(data.country || "");

        setExistingPhoto(data.photo || "");
        if (data.photo) setPreview(data.photo);

        setExistingBg(data.background || "");
        if (data.background) setBgPreview(data.background);

        setOverlay(data.overlay || "light");
      }
    };

    load();
  }, [user]);

  // 💾 SAVE
  const handleSave = async () => {
    if (!user || !name) return;

    setLoading(true);

    try {
      let photoURL = existingPhoto;
      let bgURL = existingBg;

      // PROFILE UPLOAD
      if (photo) {
        const fileRef = ref(
          storage,
          `users/${user.uid}/profile/${Date.now()}_${photo.name}`
        );
        await uploadBytes(fileRef, photo);
        photoURL = await getDownloadURL(fileRef);
      }

      // BACKGROUND UPLOAD
      if (bgImage) {
        const bgRef = ref(
          storage,
          `users/${user.uid}/background/${Date.now()}_${bgImage.name}`
        );
        await uploadBytes(bgRef, bgImage);
        bgURL = await getDownloadURL(bgRef);
      }

      await setDoc(
        doc(db, "users", user.uid),
        {
          name,
          country,
          photo: photoURL,
          background: bgURL,
          overlay,
          updatedAt: new Date()
        },
        { merge: true }
      );

      navigate("/home");

    } catch (err) {
      console.error("SAVE ERROR:", err);
      alert(err.message);
    }

    setLoading(false);
  };

if (!user) {
  return (
    <div style={styles.loadingScreen}>
      Loading...
    </div>
  );
}

  return (
    <div style={styles.page}>
      <div style={styles.card}>

<h2 style={styles.title}>
  {isEdit ? "Update your profile" : "Create your profile"}
</h2>

        {isEdit && (
  <button
    onClick={() => navigate("/home")}
    style={styles.closePageButton}
  >
    ×
  </button>
)}

        {/* PROFILE PHOTO */}
<div style={styles.photoWrap}>
  <p style={styles.label}>Give your space a face:</p>

  {preview ? (
    <img src={preview} style={styles.avatar} />
  ) : (
    <div style={styles.avatarPlaceholder}></div>
  )}

  <div style={styles.photoAction}>
    <input
      type="file"
      id="photoUpload"
      onChange={handlePhoto}
      style={{ display: "none" }}
    />

    <label htmlFor="photoUpload" style={styles.fileButton}>
      {isEdit ? "Change your photo" : (photo ? "Change your photo" : "Add your photo")}
    </label>
  </div>

</div>

<p style={styles.minilabel}>Name:</p>
<div style={styles.fieldWrap}>
  <input
    placeholder="Enter your name"
    value={name}
    onChange={(e) => setName(e.target.value)}
    style={styles.input}
  />
</div>

<p style={styles.minilabel}>Country:</p>
<div style={styles.fieldWrap} ref={dropdownRef}>
  <input
    placeholder="Enter your country"
    value={country}
    onBlur={() => setTimeout(() => closeDropdown(), 150)}
    onChange={(e) => handleCountryChange(e.target.value)}
    style={{
      ...styles.input,
      width: "100%"
    }}
  />

  {filteredCountries.length > 0 && (
    <div style={styles.dropdown}>
      {filteredCountries.map((c, i) => (
        <div key={i} style={styles.dropdownItem} onClick={() => handleSelectCountry(c)}>
          {c}
        </div>
      ))}
    </div>
  )}
</div>

<div style={styles.bgWrap}>
  <p style={styles.label}>Choose a wallpaper for your home:</p>

{true ? (
<div style={styles.bgPreviewWrap}>

  {/* IMAGE MODE */}
  {bgMode === "image" && bgPreview && (
    <img src={bgPreview} style={styles.bgBox} />
  )}

  {/* COLOR MODE */}
  {bgMode === "color" && (
    <div
      style={{
        ...styles.bgBox,
        background: bgColor
      }}
    />
  )}

  {/* EMPTY STATE */}
  {bgMode === "image" && !bgPreview && (
    <div style={styles.bgPlaceholder}>
      <div style={styles.bgLines}>
        <div style={styles.bgLine}></div>
        <div style={styles.bgLine}></div>
      </div>
    </div>
  )}

  {/* OVERLAY */}
  {overlay !== "none" && (
    <div
      style={{
        ...styles.bgOverlay,
        background:
          overlay === "light"
            ? "rgba(255,255,255,0.4)"
            : "rgba(0,0,0,0.4)"
      }}
    />
  )}

  <div style={styles.bgLinesOverlay}>
    <div style={styles.bgLine}></div>
    <div style={styles.bgLine}></div>
  </div>

</div>
) : (
  <div style={styles.bgPlaceholder}>
    <div style={styles.bgLines}>
      <div style={styles.bgLine}></div>
      <div style={styles.bgLine}></div>
    </div>
  </div>
)}

  <input
    type="file"
    id="bgUpload"
    onChange={handleBgImage}
    style={{ display: "none" }}
  />

<div style={styles.bgButtonRow}>

  <label htmlFor="bgUpload" style={styles.fileButton}>
    {bgMode === "image" && (bgImage || bgPreview || existingBg)
  ? "Change image"
  : "Select image"}
  </label>

  <button
    type="button"
    onClick={() => setShowColorPicker(true)}
    style={styles.fileButton}
  >
    {bgMode === "color"
  ? "Change color"
  : "Select color"}
  </button>

{showColorPicker && (
  <div
    style={styles.modalOverlay}
    onClick={() => setShowColorPicker(false)}
  >
    <div
      style={styles.modalBox}
      onClick={(e) => e.stopPropagation()}
    >
      <button
  onClick={() => setShowColorPicker(false)}
  style={styles.closeButton}
>
  ×
</button>

      <p style={styles.modalTitle}>Select color</p>

      <div style={styles.pickerWrap}>
<HexColorPicker
  color={bgColor}
  onChange={(color) => {
    setBgColor(color);
    setBgMode("color");   // 🔥 FORCE MODE
    setBgImage(null);
    setBgPreview("");
  }}
/>
      </div>

<input
  value={bgColor}
  onChange={(e) => {
    setBgColor(e.target.value);
    setBgMode("color");
    setBgImage(null);
    setBgPreview("");
  }}
  style={styles.hexInput}
/>

      <button
        onClick={() => setShowColorPicker(false)}
        style={styles.doneButton}
      >
        Select
      </button>

    </div>
  </div>
)}
</div>
</div>


        {/* OVERLAY */}
        <div>
          <p style={styles.overlaylabel}>Select an overlay to your wallpaper:</p>

<div style={styles.overlayWrap}>
  <div
    onClick={() => setOverlay("none")}
    style={overlay === "none" ? styles.activeOverlayBtn : styles.overlayBtn}
  >
    None
  </div>

  <div
    onClick={() => setOverlay("light")}
    style={overlay === "light" ? styles.activeOverlayBtn : styles.overlayBtn}
  >
    White
  </div>

  <div
    onClick={() => setOverlay("dark")}
    style={overlay === "dark" ? styles.activeOverlayBtn : styles.overlayBtn}
  >
    Black
  </div>
</div>
        </div>


<button
  style={{
    ...styles.button,
    cursor: name && !loading ? "pointer" : "not-allowed",
    opacity: loading ? 0.5 : name ? 1 : 0.5,
    pointerEvents: name && !loading ? "auto" : "none"
  }}
  onClick={handleSave}
  disabled={!name || loading}
>
  {loading
    ? isEdit
      ? "Updating"
      : "Creating"
    : isEdit
      ? "Update"
      : "Create"}
</button>

      </div>
    </div>
  );
}

const styles = {
  center: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },

  page: {
    height: "100vh",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    overflowY: "auto",
    backgroundColor: "rgba(255, 255, 255, 0.4)",
  },

card: {
  backgroundColor: "rgba(255, 255, 255, 0.4)",
  position: "relative",
  paddingTop: "30px",
  paddingBottom: "30px",
  width: "100%",
  maxWidth: "800px",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",   // 🔥 center horizontally
  textAlign: "center",    // 🔥 center text
  fontFamily: "'Inter', sans-serif",
  color: "#000",
  margin: "0px auto 20px"
},

title: {
  fontSize: "25px",
  fontWeight: "600",
  marginBottom: "10px",
  letterSpacing: "0.3px",
  color: "#000",
  fontFamily: "'Inter', sans-serif",
},

closePageButton: {
  position: "absolute",
  top: "15px",
  right: "15px",
  background: "transparent",
  border: "none",
  fontSize: "26px",
  cursor: "pointer",
  color: "#555",
},

photoWrap: {
  marginBottom: "20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center"
},

  avatar:{width:"120px",height:"120px",borderRadius:"50%",objectFit:"cover",margin:"0 auto 15px"},

  avatarPlaceholder:{width:"120px",height:"120px",borderRadius:"50%",background:"#eee",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 15px",fontSize:"24px"},

  bgPreview:{width:"100%",height:"120px",objectFit:"cover",borderRadius:"10px",marginBottom:"10px"},
  
  input: {
  width: "100%",
  maxWidth: "310px",
  padding: "14px 16px",
  marginBottom: "12px",
  borderRadius: "15px",
  border: "1px solid #ccc",
  textAlign: "center",
  fontSize: "15px",
  fontFamily: "'Inter', sans-serif",
  boxSizing: "border-box",
  backgroundColor: "rgba(255, 255, 255, 0.4)",
},

dropdown: {
  position: "absolute",
  width: "100%",
  maxWidth: "420px",
  left: "50%",
  transform: "translateX(-50%)",
  background: "#fff",
  border: "1px solid #ccc",
  borderRadius: "10px",
  zIndex: 10
},

dropdownItem:{padding:"10px",cursor:"pointer"},

overlayWrap:{display:"flex",justifyContent:"center",gap:"15px",margin:"0 auto 25px"},

overlayBtn: {
  padding: "8px 20px",
  borderRadius: "20px",
  background: "#eee",
  cursor: "pointer",
  fontSize: "0.85rem",
  color: "#000",
  textAlign: "center",
  transition: "0.2s",
},

activeOverlayBtn: {
  padding: "8px 20px",
  borderRadius: "20px",
  background: "#696969",
  color: "#fff",
  cursor: "pointer",
  fontSize: "0.85rem"
},

  label:{
  fontSize:"15px",
  margin: "30px auto 15px",
  color: "#000",
},

  minilabel:{
  fontSize:"12px",
  margin: "0px auto 5px",
  color: "#000",
},

  overlaylabel:{
  fontSize:"15px",
  margin: "35px auto 15px",
  color: "#000",
},

fieldWrap: {
  width: "100%",
  maxWidth: "310px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative"
},

fileButton: {
  display: "inline-block",
  padding: "10px 16px",
  borderRadius: "20px",
  background: "#eee",
  cursor: "pointer",
  fontSize: "0.85rem",
  color: "#000",
  margin:"0 auto 20px",
  border: "none",        // ✅ removes black border
  outline: "none"     
},

  button:{marginTop:"20px",padding:"15px",width:"100%",maxWidth:"290px",borderRadius:"25px",border:"none",background:"#696969",color:"#fff"},

  buttonDisabled: {
  marginTop: "20px",
  padding: "15px",
  width: "50%",
  borderRadius: "25px",
  border: "none",
  cursor: "not-allowed",
  opacity: 0.5
},

bgWrap: {
  width: "100%",
  maxWidth: "310px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: "-15px",
},

bgPreviewWrap: {
  width: "100%",
  height: "180px",
  position: "relative",
  overflow: "hidden",   // 🔥 IMPORTANT FIX
  borderRadius: "15px",
  margin: "0 auto 15px",
},

bgBox: {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
  position: "absolute",
  inset: 0,
},

bgOverlay: {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 2,
  transition: "0.25s ease",
  position: "absolute",
  inset: 0,
  zIndex: 2,
},

bgPlaceholder: {
  width: "100%",
  height: "180px",
  background: "#eee",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin:"0 auto 15px",
  position: "relative",
  borderRadius: "15px",
},

bgLines: {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "120px",
  height: "100%",   // 🔥 IMPORTANT (fills the box)
  width: "100%"
},

bgLine: {
  width: "0px", // 🔥 important (we use border instead)
  height: "100%",
  borderLeft: ".5px dotted #999",
  opacity: 0.8
},

bgLinesOverlay: {
  position: "absolute",
  inset: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "120px",
  zIndex: 3,
  pointerEvents: "none"
},

bgButtonRow: {
  display: "flex",
  gap: "10px",
  justifyContent: "center",
  alignItems: "center"
},

modalOverlay: {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999
},

modalBox: {
  width: "250px",
  background: "#fff",
  padding: "20px",
  borderRadius: "15px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  textAlign: "center",
  position: "relative" // 🔥 REQUIRED
},

modalTitle: {
  margin: 0,
  fontSize: "16px",
  fontWeight: "600",
  textAlign: "center"
},

pickerWrap: {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%"
},

hexInput: {
  width: "40%",
  textAlign: "center",
  fontFamily: "monospace",
  padding: "10px",
  borderRadius: "15px",
  border: "1px solid #ccc",
  outline: "none",
  display: "block"
},

doneButton: {
  width: "65%",
  padding: "12px",
  borderRadius: "15px",
  border: "none",
  background: "#696969",
  color: "#fff",
  cursor: "pointer"
},

closeButton: {
  position: "absolute",
  top: "10px",
  right: "12px",
  background: "transparent",
  border: "none",
  fontSize: "20px",
  cursor: "pointer",
  color: "#555"
},

loadingScreen: {
  height: "100vh",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
},
};

export default Create;